import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useModal } from '../stores/uiStore';
import { useAuth } from '../contexts/AuthContext';
import { useHasPermission } from '../stores/authStore';
import { 
  useDeleteAssessment, 
  usePublishAssessment, 
  useUnpublishAssessment
} from '../hooks/api';
import { 
  CreateAssessmentModal, 
  EditAssessmentModal, 
  AssessmentDetailModal 
} from '../components/assessments';
import AssessmentDashboard from '../components/assessments/AssessmentDashboard';

const Assessments: React.FC = () => {
  const { user: _user } = useAuth();
  const createModal = useModal('create-assessment');
  const editModal = useModal('edit-assessment');
  const detailModal = useModal('assessment-detail');
  
  // Permissions
  const canCreateAssessment = useHasPermission('assessment.create');
  const _canEditAssessment = useHasPermission('assessment.update');
  const _canDeleteAssessment = useHasPermission('assessment.delete');
  const _canPublishAssessment = useHasPermission('assessment.publish');
  
  // API hooks
  const deleteMutation = useDeleteAssessment();
  const publishMutation = usePublishAssessment();
  const unpublishMutation = useUnpublishAssessment();
  
  // Handle actions
  const handleDeleteAssessment = (assessmentId: string) => {
    const confirmed = window.confirm('Are you sure you want to delete this assessment? This action cannot be undone.');
    if (confirmed) {
      deleteMutation.mutate(assessmentId);
    }
  };
  
  const handlePublishAssessment = (assessmentId: string) => {
    publishMutation.mutate(assessmentId);
  };
  
  const handleUnpublishAssessment = (assessmentId: string) => {
    unpublishMutation.mutate(assessmentId);
  };
  
  const handleCreateAssessment = () => {
    createModal.open();
  };
  
  const handleEditAssessment = (assessmentId: string) => {
    editModal.open({ assessmentId });
  };
  
  const handleViewAssessment = (assessmentId: string) => {
    detailModal.open({ assessmentId });
  };

  // Deep-link support: opening /assessments?id=<id> auto-shows the detail
  // modal so dashboard rows (and any future inbound links) can scroll
  // straight to a specific assessment without a second click. We leave
  // the param in the URL on purpose -- stripping it via setSearchParams
  // races with the modal-open call and closes the modal before it
  // mounts; cleaner to keep the deep link addressable.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      detailModal.open({ assessmentId: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot on mount
  }, []);
  
  
  return (
    <div className="max-w-7xl mx-auto">
      <AssessmentDashboard
        onCreateAssessment={handleCreateAssessment}
        onViewAssessment={handleViewAssessment}
        canCreateAssessment={canCreateAssessment}
      />
      
      {/* Modals */}
      <CreateAssessmentModal />
      <EditAssessmentModal />
      <AssessmentDetailModal
        onEdit={handleEditAssessment}
        onDelete={handleDeleteAssessment}
        onPublish={handlePublishAssessment}
        onUnpublish={handleUnpublishAssessment}
      />
    </div>
  );
};

export default Assessments;
